export const GA_MEASUREMENT_ID = "G-M93L2TCE8F";

const GA_SCRIPT_ID = "google-analytics-gtag";
const GA_COOKIE_NAMES = ["_ga", `_ga_${GA_MEASUREMENT_ID.replace("G-", "")}`];

type GtagCommand = [command: string, ...args: unknown[]];

declare global {
  interface Window {
    dataLayer?: GtagCommand[];
    gtag?: (...args: GtagCommand) => void;
    __PASSCAR_GA_INITIALIZED?: boolean;
  }
}

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

export function shouldEnableGoogleAnalytics() {
  return Boolean(
    GA_MEASUREMENT_ID &&
      isBrowser() &&
      (import.meta.env.PROD || import.meta.env.VITE_GA_DEBUG === "true"),
  );
}

export function loadGoogleAnalytics() {
  if (!shouldEnableGoogleAnalytics()) return false;

  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || ((...args: GtagCommand) => {
    window.dataLayer?.push(args);
  });

  if (!document.getElementById(GA_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  if (!window.__PASSCAR_GA_INITIALIZED) {
    window.gtag("js", new Date());
    window.gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
    window.__PASSCAR_GA_INITIALIZED = true;
  }

  return true;
}

export function trackPageView(path: string) {
  if (!loadGoogleAnalytics() || !window.gtag) return;

  window.gtag("event", "page_view", {
    send_to: GA_MEASUREMENT_ID,
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function disableGoogleAnalytics() {
  if (!isBrowser()) return;

  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  window.gtag?.("consent", "update", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  GA_COOKIE_NAMES.forEach(removeCookie);
}

function removeCookie(name: string) {
  const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
  document.cookie = base;

  getCookieDomains().forEach((domain) => {
    document.cookie = `${base}; domain=${domain}`;
  });
}

function getCookieDomains() {
  const hostname = window.location.hostname;

  if (hostname === "localhost" || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return [];
  }

  const parts = hostname.split(".");
  const domains = new Set([hostname, `.${hostname}`]);

  for (let index = 1; index < parts.length - 1; index += 1) {
    domains.add(`.${parts.slice(index).join(".")}`);
  }

  return Array.from(domains);
}
